import React from 'react';
import styled from 'styled-components';
import { themeColor } from '../Theming';
import { nationalRailImages } from '../Theming/images';
import usePageTitle from '../Common/Hooks/usePageTitle';

import { usePageViewTracking, useQuery } from '../Common/Hooks';
import {
  ANALYTICS_ACTION_LABELS,
  ANALYTICS_PAGE_NAMES,
  PartnerNames,
  trackClickedActionEvent,
} from '../Common/Analytics/utils';
import { ContentPanel } from './Components';

const Container = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background-color: white;
  background-image: url(${nationalRailImages.nationalRailBackground});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  @media (max-height: 950px) {
    height: auto;
    min-height: 100vh;
    overflow-y: auto;
  }
`;

const Button = styled.button`
  background-color: ${themeColor('navy')};
  color: ${themeColor('gold')};
  border: none;
  padding: 1.2rem 2rem;
  font-size: 1.4rem;
  font-weight: 600;
  font-family: 'Century Gothic', sans-serif;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 300px;
  height: 61px;
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const BoldText = styled.p`
  font-weight: 700;
  font-size: 28px;
  font-family: 'Century Gothic', sans-serif;
  color: ${themeColor('navy')};
  text-align: center;
  margin-bottom: 15px;
`;

const SmallText = styled.p`
  font-weight: 400;
  font-size: 12px;
  font-family: 'Century Gothic', sans-serif;
  color: ${themeColor('navy')};
  text-align: center;
  margin-top: 35px;

  & a {
    color: ${themeColor('navy')};
    text-decoration: underline;
    cursor: pointer;
  }
`;

const FullWidthImage = styled.img`
  width: calc(100% + 80px);
  height: auto;
  display: block;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 20px;
`;

interface ExtensionInstalledProps {
  partnerName?: PartnerNames;
}

const ExtensionInstalled: React.FC<ExtensionInstalledProps> = ({
  partnerName = 'NationalRail',
}) => {
  const query = useQuery();
  const utm_campaign = query.get('utm_campaign') as PartnerNames;
  const effectivePartnerName = utm_campaign ?? partnerName;

  usePageViewTracking(
    ANALYTICS_PAGE_NAMES.chrome_desktop_success_page,
    effectivePartnerName
  );

  usePageTitle('Extension Installed');

  const onCTAClicked = () => {
    trackClickedActionEvent(
      query,
      effectivePartnerName,
      ANALYTICS_ACTION_LABELS.back_to_partner_site,
      ANALYTICS_PAGE_NAMES.chrome_desktop_success_page
    );
    window.location.href = 'https://nationalrail.co.uk';
  };

  return (
    <Container>
      <ContentPanel>
        <ContentContainer>
          <BoldText>{`All done! 🎉`}</BoldText>
          <FullWidthImage
            src={nationalRailImages.popularBrands}
            alt="Popular Brands"
          />
          <Button onClick={onCTAClicked}>Back to National Rail</Button>
          <SmallText>
            Do you have any questions? feel free to visit our{' '}
            <a
              href="https://event.kindred.co/nationalrail-faqs"
              target="_blank"
              rel="noopener noreferrer"
            >
              FAQs page
            </a>
            . Here, you will find most of the answers about Kindred and how to
            get the most out of it.
          </SmallText>
        </ContentContainer>
      </ContentPanel>
    </Container>
  );
};

export default ExtensionInstalled;
